import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import "./BrandTable.scss";
import TableHeaderRow from "../../../../components/tables/TableHeaderRow";
import TableHeaderCell from "../../../../components/tables/TableHeaderCell";
import TableDataRow from "../../../../components/tables/TableDataRow";
import TableDataCell from "../../../../components/tables/TableDataCell";
import { IBrandData } from "./BrandList";
import ProfileImage from "../../../../components/profileImage/ProfileImage";
import { getCdnImageUrl, getImageUrl } from "../../../../utils/helpers/common";

const BrandTable = ({ listData }: { listData: IBrandData[] }) => {
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();

  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    return moment(dateString).format("MMMM D, YYYY");
  };

  const formatTime = (dateString?: string) => {
    if (!dateString) return "-";
    return moment(dateString).format("hh:mm A");
  };

  const getStatusConfig = (status?: string) => {
    const normalized = status?.toLowerCase() || "";

    const label = normalized
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());

    let className = "inactive";

    if (normalized === "active") {
      className = "active";
    } else if (normalized === "agency_draft") {
      className = "agency-draft";
    } else if (normalized === "admin_draft") {
      className = "admin-draft";
    } else if (normalized === "deactivated") {
      className = "deactivated";
    } else if (normalized === "brand_locked") {
      className = "locked";
    } else if (normalized === "brand_archive") {
      className = "archived";
    }

    return {
      label,
      className,
    };
  };

  const formatStatus = (status?: string) => {
    if (!status) return "-";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const COUNTRY_OPTIONS = [
    { value: "australia", label: "Australia" },
    { value: "new_zealand", label: "New Zealand" },
  ];

  const getCountryLabel = (value?: string) => {
    if (!value) return "-";

    const country = COUNTRY_OPTIONS.find((item) => item.value === value);

    return country ? country.label : value;
  };

  const getBrandLogoUrl = (brandLogo: any) => {
    if (!brandLogo) return "";
    if (brandLogo.storage_key) return getCdnImageUrl(brandLogo.storage_key);
    if (brandLogo.compressed_file_path)
      return getImageUrl(brandLogo.compressed_file_path);
    if (brandLogo.original_file_path)
      return getImageUrl(brandLogo.original_file_path);
    return "";
  };

  return (
    <div className="brand-table-container">
      <table>
        <TableHeaderRow>
          <TableHeaderCell minWidth={200}>BRAND LOGO</TableHeaderCell>
          <TableHeaderCell minWidth={150}>BRAND NAME</TableHeaderCell>
          <TableHeaderCell minWidth={130}>COUNTRY</TableHeaderCell>
          <TableHeaderCell minWidth={140}>STATUS</TableHeaderCell>
          <TableHeaderCell minWidth={140}>CREATED ON</TableHeaderCell>
          <TableHeaderCell minWidth={140}>UPDATED ON</TableHeaderCell>
          <TableHeaderCell minWidth={80}>ACTION</TableHeaderCell>
        </TableHeaderRow>

        {listData?.map((data) => (
          <div key={data._id} className="row-wrapper">
            <TableDataRow
              className="contain-row clickable-row"
              onClick={() => navigate(`/brand/view/${data._id}`)}
            >
              <TableDataCell minWidth={200}>
                <div className="brand-logo-wrapper">
                  {data.brand_logo ? (
                    <img
                      src={getBrandLogoUrl(data.brand_logo)}
                      alt="brand-logo"
                    />
                  ) : (
                    "-"
                  )}
                </div>
              </TableDataCell>

              <TableDataCell minWidth={150}>
                <div className="brand-name">{data.brand_name || "-"}</div>
              </TableDataCell>

              <TableDataCell minWidth={130}>
                <div className="country-text">
                  {getCountryLabel(data.brand_country)}
                </div>
              </TableDataCell>

              <TableDataCell minWidth={140}>
                {(() => {
                  const status = getStatusConfig(data.status);

                  return (
                    <div className={`status-badge ${status.className}`}>
                      {status.label}
                    </div>
                  );
                })()}
              </TableDataCell>

              <TableDataCell minWidth={140}>
                <div className="date-contain">
                  <p className="date-text">{formatDate(data.createdAt)}</p>
                  <p className="time-text">{formatTime(data.createdAt)}</p>
                </div>
              </TableDataCell>

              <TableDataCell minWidth={140}>
                <div className="date-contain">
                  <p className="date-text">{formatDate(data.updatedAt)}</p>
                  <p className="time-text">{formatTime(data.updatedAt)}</p>
                </div>
              </TableDataCell>

              <TableDataCell minWidth={80} className="action-td">
                <div
                  className="action-menu-wrapper"
                  ref={openMenuId === data._id ? menuRef : null}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div
                    className="action-menu-btn"
                    onClick={() =>
                      setOpenMenuId(openMenuId === data._id ? null : data._id)
                    }
                  >
                    &#8942;
                  </div>

                  {openMenuId === data._id && (
                    <div className="action-dropdown">
                      <div
                        className="action-item font-inter-400"
                        onClick={() => navigate(`/brand/view/${data._id}`)}
                      >
                        View
                      </div>
                      {/* <div
                        className="action-item font-inter-400"
                        onClick={() => navigate(`/brand/view/${data._id}`)}
                      >
                        Edit
                      </div> */}
                    </div>
                  )}
                </div>
              </TableDataCell>
            </TableDataRow>
          </div>
        ))}
      </table>
    </div>
  );
};

export default BrandTable;
